import React, { useState } from 'react';
import { Stack } from '@mui/material';
import { gql, useQuery } from '@apollo/client';
import { useHistory } from 'react-router-dom';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { MUIBaseEntity, MUITable } from 'components/shared/MUITable';
import { generateProposalPageURL } from 'utils/generateProposalPageURL';
import { APIRequestPagination, APIRequestSort } from 'global';
import { createTableColumns } from './tableConfig';
import { InventoryTableFilters } from './InventoryTableFilters';
import { InventoryItem } from '@trnsact/trnsact-shared-types/dist/generated';

const Q_INVENTORY_LIST = gql`
  query GetInventoryList($input: InventoryListInput!) {
    inventoryList(input: $input) {
      data {
        condition
        description
        make
        mileage
        model
        retailPrice
        source
        stockNumber
        vin
        year
        inventoryId
        equipmentId
        status
      }
      page
      perPage
      total
      totalPages
    }
  }
`;

interface Props {
  accountId: string;
}

interface InventoryListResponse {
  page: number;
  total: number;
  perPage: number;
  totalPages: number;
  data: InventoryItem[];
}

export interface InventoryItemEntity extends MUIBaseEntity, InventoryItem {}

export const InventoryTable = ({ accountId }: Props) => {
  const history = useHistory();

  const form = useForm({
    defaultValues: {
      filters: {},
    },
  });

  const filters = useWatch({ control: form.control, name: 'filters' });

  const [pagination, setPagination] = useState<APIRequestPagination>({ page: 1, perPage: 15 });
  const [sorting, setSorting] = useState<APIRequestSort>({ key: 'make', value: 'asc' });

  const { data, loading } = useQuery<{ inventoryList: InventoryListResponse }>(Q_INVENTORY_LIST, {
    variables: {
      accountId,
      input: {
        accountId,
        pagination,
        sort: sorting,
        filters,
      },
    },
  });

  const columns = createTableColumns({ useHistory: history });

  return (
    <FormProvider {...form}>
      <Stack gap="0.5rem" height="100%">
        <InventoryTableFilters />

        <MUITable<InventoryItemEntity>
          columns={columns}
          isLoading={loading}
          sorting={{ ...sorting, onChange: setSorting }}
          onRowClick={(_event, row) => {
            if (row.equipmentId) {
              history.push(`/equipment/${row.equipmentId}/details`);
            }
          }}
          tableData={
            data?.inventoryList?.data?.map(inventoryItem => ({
              id: inventoryItem.inventoryId!,
              condition: inventoryItem.condition,
              description: inventoryItem.description ?? '',
              make: inventoryItem.make,
              mileage: inventoryItem.mileage,
              model: inventoryItem.model,
              retailPrice: inventoryItem.retailPrice,
              source: inventoryItem.source,
              stockNumber: inventoryItem.stockNumber,
              vin: inventoryItem.vin,
              year: inventoryItem.year,
              equipmentId: inventoryItem.equipmentId,
              inventoryId: inventoryItem.inventoryId,
              status: inventoryItem.status,
            })) ?? []
          }
          pagination={{
            ...pagination,
            total: data?.inventoryList?.total ?? 0,
            onChange: nextValue => setPagination(prev => ({ ...prev, ...nextValue })),
          }}
        />
      </Stack>
    </FormProvider>
  );
};
